
import React, { useEffect, useState, useRef } from 'react';

interface AdSenseBlockProps {
  clientId: string;
  slotId: string;
  format?: 'auto' | 'fluid' | 'rectangle';
  responsive?: boolean;
  className?: string;
}

const AdSenseBlock: React.FC<AdSenseBlockProps> = ({ clientId, slotId, format = 'auto', responsive = true, className }) => {
  const [navKey, setNavKey] = useState(0);
  const [status, setStatus] = useState<'loading' | 'filled' | 'unfilled'>('loading');
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const insRef = useRef<HTMLModElement>(null);

  // List of mock ads to cycle through on navigation in preview/local mode
  const mockAds = [
    {
      title: "Hospedagem Cloud Premium Inteligente",
      desc: "Sua plataforma de automação ultra veloz com redundância global e suporte técnico 24/7. Ganhe 50% de desconto no primeiro ano usando o cupom AUTOMACAO50.",
      cta: "Conhecer Planos",
      url: "https://cloud-premium.example.com",
      color: "from-blue-600 to-indigo-700"
    },
    {
      title: "Disparador Automático de WhatsApp",
      desc: "Conecte seu CRM, agende envios em massa de forma humanizada e automatize suas vendas em menos de 5 minutos sem risco de banimento. Teste grátis!",
      cta: "Instalar Extensão",
      url: "https://zapflow.example.com",
      color: "from-emerald-600 to-teal-700"
    },
    {
      title: "IA Generativa para Criar Aplicativos",
      desc: "Transforme ideias simples em sistemas completos, apps nativos e integrações ricas sem programar. O futuro do desenvolvimento No-Code está aqui.",
      cta: "Começar Grátis",
      url: "https://ia-studio.example.com",
      color: "from-purple-600 to-violet-700"
    }
  ];

  const mockAdIndex = navKey % mockAds.length;
  const currentMockAd = mockAds[mockAdIndex];

  useEffect(() => {
    const handleNav = () => {
      setNavKey(prev => prev + 1);
      setStatus('loading');
    };
    window.addEventListener('app-route-changed', handleNav);
    window.addEventListener('popstate', handleNav);
    return () => {
      window.removeEventListener('app-route-changed', handleNav);
      window.removeEventListener('popstate', handleNav);
    };
  }, []);

  // Check for local or preview domains
  const isLocalOrPreview = typeof window !== 'undefined' && (
    window.location.hostname.includes('localhost') ||
    window.location.hostname.includes('127.0.0.1') ||
    window.location.hostname.includes('run.app') ||
    window.location.hostname.includes('github.dev') ||
    window.location.hostname.includes('stackblitz') ||
    window.location.hostname.includes('webcontainer')
  );

  useEffect(() => {
    if (isLocalOrPreview) {
      setIsPreviewLoading(true);
      const timer = setTimeout(() => {
        setIsPreviewLoading(false);
        setStatus('filled');
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [navKey, isLocalOrPreview]);

  useEffect(() => {
    if (isLocalOrPreview) return;
    if (!clientId || !slotId) return;

    let timer: any;
    try {
      // Delay so React commits the <ins> tag to the DOM properly with visible dimensions
      timer = setTimeout(() => {
        // @ts-ignore
        const adsbygoogle = window.adsbygoogle || [];
        adsbygoogle.push({});
      }, 150);
    } catch (e) {
      console.error("AdSense Error:", e);
      setStatus('unfilled');
    }

    return () => clearTimeout(timer);
  }, [slotId, clientId, navKey, isLocalOrPreview]);

  useEffect(() => {
    if (isLocalOrPreview) return;
    const insElement = insRef.current;
    if (!insElement) return;

    // Monitor internal changes and class/style modifications by Google AdSense
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        const adStatus = insElement.getAttribute('data-ad-status');
        const googleStatus = insElement.getAttribute('data-adsbygoogle-status');
        
        if (adStatus === 'unfilled') {
          setStatus('unfilled');
        } else if (adStatus === 'filled') {
          setStatus('filled');
        } else if (googleStatus === 'done') {
          // If AdSense says done but status isn't filled, check if an iframe with content exists
          const iframe = insElement.querySelector('iframe');
          if (iframe) {
            const rect = iframe.getBoundingClientRect();
            if (rect.height > 10) {
              setStatus('filled');
            } else {
              // Quick delayed check
              setTimeout(() => {
                const updatedRect = iframe.getBoundingClientRect();
                if (updatedRect.height > 10) {
                  setStatus('filled');
                } else {
                  setStatus('unfilled');
                }
              }, 400);
            }
          } else {
            setStatus('unfilled');
          }
        }
      });
    });

    observer.observe(insElement, {
      attributes: true,
      attributeFilter: ['data-ad-status', 'data-adsbygoogle-status', 'style'],
      childList: true,
      subtree: true
    });

    // Safety timeout: if after 4 seconds it is still not filled, hide it to prevent blank spaces.
    const safetyTimeout = setTimeout(() => {
      const adStatus = insElement.getAttribute('data-ad-status');
      const googleStatus = insElement.getAttribute('data-adsbygoogle-status');
      const iframe = insElement.querySelector('iframe');
      
      if (adStatus === 'unfilled') {
        setStatus('unfilled');
      } else if (adStatus === 'filled') {
        setStatus('filled');
      } else if (googleStatus === 'done' && iframe && iframe.getBoundingClientRect().height > 10) {
        setStatus('filled');
      } else if (!googleStatus || !iframe || iframe.getBoundingClientRect().height <= 10) {
        // Not processed, no iframe, or iframe is empty/collapsed
        setStatus('unfilled');
      }
    }, 4000);

    return () => {
      observer.disconnect();
      clearTimeout(safetyTimeout);
    };
  }, [navKey, clientId, slotId, isLocalOrPreview]);

  // Se não houver IDs configurados, não renderiza nada (nem espaço em branco)
  if (!clientId || !slotId) return null;

  const isUnfilled = status === 'unfilled';
  const isLoading = status === 'loading';
  const isFilled = status === 'filled';

  // Render mock ad for preview environments so they can verify that the ads load and refresh on navigation
  if (isLocalOrPreview) {
    if (isPreviewLoading) {
      return (
        <div className={`w-full my-6 flex flex-col items-center justify-center border border-gray-100 rounded-xl bg-slate-50 p-4 animate-pulse ${format === 'rectangle' ? 'max-w-sm h-[250px]' : 'h-[105px]'} ${className || ''}`}>
          <div className="flex justify-between items-center w-full mb-2">
            <div className="h-2.5 bg-gray-200 rounded-full w-1/4"></div>
            <div className="h-2.5 bg-indigo-100 rounded-full w-24"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded-full w-3/4 mb-3"></div>
          <div className="h-2.5 bg-gray-200 rounded-full w-full mb-2"></div>
          <div className="h-2.5 bg-gray-200 rounded-full w-5/6"></div>
        </div>
      );
    }

    if (format === 'rectangle') {
      return (
        <div className={`w-full max-w-sm my-6 p-5 border border-dashed border-indigo-200 rounded-2xl bg-white shadow-sm flex flex-col justify-between h-[250px] relative group overflow-hidden ${className || ''}`}>
          <div className="flex justify-between items-start">
            <span className="text-[9px] font-black tracking-widest text-slate-400 uppercase bg-slate-100 px-1.5 py-0.5 rounded">AdSense Real</span>
            <span className="text-[9px] font-black tracking-widest text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full animate-bounce">Preview Refrescado ✓</span>
          </div>
          
          <div className="flex-1 my-3 flex flex-col justify-center">
            <h4 className="text-sm font-black text-slate-800 leading-tight mb-2 group-hover:text-primary transition-colors">
              {currentMockAd.title}
            </h4>
            <p className="text-[11px] text-slate-500 line-clamp-3 leading-relaxed">
              {currentMockAd.desc}
            </p>
          </div>

          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-slate-400 font-mono truncate max-w-[150px]">{currentMockAd.url}</span>
            <button className={`px-4 py-2 bg-gradient-to-r ${currentMockAd.color} text-white font-extrabold text-[10px] uppercase tracking-wider rounded-lg shadow-sm hover:brightness-105 transition-all`}>
              {currentMockAd.cta}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={`w-full my-6 p-4 border border-dashed border-indigo-200 rounded-xl bg-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3 relative group overflow-hidden ${className || ''}`}>
        <div className="absolute top-0 right-0 p-1 flex items-center gap-1.5 z-10 bg-indigo-50/50 rounded-bl-lg">
          <span className="text-[8px] font-bold tracking-wider text-slate-400 uppercase">AdSense Real</span>
          <span className="text-[8px] font-black tracking-wider text-indigo-600 bg-indigo-100/50 px-1.5 py-0.2 rounded-full">Preview Refrescado ✓</span>
        </div>
        
        <div className="flex-1 min-w-0 pr-4 mt-2 md:mt-0">
          <h4 className="text-xs font-black text-slate-800 leading-none mb-1 group-hover:text-primary transition-colors">
            {currentMockAd.title}
          </h4>
          <p className="text-[10px] text-slate-500 line-clamp-2 leading-normal">
            {currentMockAd.desc}
          </p>
        </div>

        <div className="flex items-center justify-between md:justify-end gap-3 shrink-0">
          <span className="text-[9px] text-slate-400 font-mono hidden md:inline">{currentMockAd.url}</span>
          <button className={`px-3 py-2 bg-gradient-to-r ${currentMockAd.color} text-white font-extrabold text-[9px] uppercase tracking-wider rounded-md shadow-sm hover:brightness-105 transition-all`}>
            {currentMockAd.cta}
          </button>
        </div>
      </div>
    );
  }

  // Estilo de dimensões mínimas para evitar que AdSense recuse renderizar por estar invisível ou com altura zero
  const minHeightVal = format === 'rectangle' ? '250px' : '90px';

  return (
    <div 
      className={`adsense-container w-full flex justify-center transition-all duration-300 ease-out relative ${
        isUnfilled 
          ? 'h-0 my-0 overflow-hidden opacity-0 pointer-events-none' 
          : 'my-6 opacity-100'
      } ${className || ''}`} 
      style={{ 
        minHeight: isUnfilled ? '0px' : minHeightVal,
      }}
      key={`container-${navKey}`}
    >
      {/* Elegante background placeholder passivo que é coberto quando o anúncio carrega */}
      {!isUnfilled && (
        <div className="absolute inset-0 bg-slate-50/40 rounded-xl flex flex-col items-center justify-center border border-slate-100/50 pointer-events-none">
          <span className="text-[9px] font-extrabold tracking-widest text-slate-300 uppercase">Publicidade</span>
          {isLoading && (
            <div className="w-4 h-4 border-2 border-slate-200 border-t-slate-400 rounded-full animate-spin mt-1.5 opacity-60"></div>
          )}
        </div>
      )}

      <ins
        ref={insRef}
        key={`ins-${navKey}`}
        className="adsbygoogle"
        style={{ 
          display: isUnfilled ? 'none' : 'block', 
          width: '100%', 
          minHeight: minHeightVal,
          zIndex: 10,
          position: 'relative'
        }}
        data-ad-client={clientId}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive={responsive ? "true" : "false"}
        data-page-url={typeof window !== 'undefined' ? window.location.href : undefined}
      />
    </div>
  );
};

export default AdSenseBlock;

